import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FaceCapture from '../components/FaceCapture';

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem',
    alignItems: 'start',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '16px',
    padding: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    color: '#00d9ff',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  input: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    background: 'rgba(0, 0, 0, 0.3)',
    color: '#fff',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.3s ease',
  },
  select: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    background: 'rgba(0, 0, 0, 0.3)',
    color: '#fff',
    fontSize: '1rem',
    outline: 'none',
  },
  button: {
    padding: '1rem 2rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
    color: '#000',
  },
  successButton: {
    background: 'linear-gradient(135deg, #00e676 0%, #00c853 100%)',
    color: '#000',
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  alert: {
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  alertSuccess: {
    background: 'rgba(0, 230, 118, 0.2)',
    color: '#00e676',
    border: '1px solid rgba(0, 230, 118, 0.3)',
  },
  alertError: {
    background: 'rgba(255, 82, 82, 0.2)',
    color: '#ff5252',
    border: '1px solid rgba(255, 82, 82, 0.3)',
  },
  faceData: {
    background: 'rgba(0, 0, 0, 0.3)',
    padding: '1rem',
    borderRadius: '8px',
    marginTop: '1rem',
  },
  badge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '600',
  },
  badgeSuccess: {
    background: 'rgba(0, 230, 118, 0.2)',
    color: '#00e676',
  },
  badgeWarning: {
    background: 'rgba(255, 193, 7, 0.2)',
    color: '#ffc107',
  },
  existingPersons: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  personChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '20px',
    margin: '0.25rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '1px solid transparent',
  },
  personChipSelected: {
    background: 'rgba(0, 217, 255, 0.2)',
    borderColor: '#00d9ff',
  },
  row: {
    display: 'flex',
    gap: '1rem',
  },
  halfWidth: {
    flex: 1,
  },
};

function AddFacePage() {
  const [formData, setFormData] = useState({
    person_id: '',
    name: '',
    email: '',
    department: '',
    designation: '',
    phone: '',
  });
  const [faceVector, setFaceVector] = useState(null);
  const [faceConfidence, setFaceConfidence] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [existingPersons, setExistingPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [mode, setMode] = useState('new'); // 'new' or 'existing'

  useEffect(() => {
    fetchPersons();
  }, []);

  const fetchPersons = async () => {
    try {
      const response = await axios.get('/api/persons');
      setExistingPersons(response.data);
    } catch (error) {
      console.error('Error fetching persons:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFaceDetected = (data) => {
    setFaceVector(data.vector);
    setFaceConfidence(data.confidence);
    setAlert({
      type: 'success',
      message: `Face captured! Confidence: ${(data.confidence * 100).toFixed(1)}%`
    });
  };

  const selectExistingPerson = (person) => {
    setSelectedPerson(person);
    setFormData({
      person_id: person.person_id,
      name: person.name,
      email: person.email || '',
      department: person.department || '',
      designation: person.designation || '',
      phone: person.phone || '',
    });
    setMode('existing');
  };

  const resetForm = () => {
    setFormData({
      person_id: '',
      name: '',
      email: '',
      department: '',
      designation: '',
      phone: '',
    });
    setFaceVector(null);
    setFaceConfidence(null);
    setSelectedPerson(null);
    setMode('new');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!faceVector) {
      setAlert({ type: 'error', message: 'Please capture a face first!' });
      return;
    }
    
    if (!formData.person_id || !formData.name) {
      setAlert({ type: 'error', message: 'Please fill in Person ID and Name!' });
      return;
    }
    
    setIsSubmitting(true);
    setAlert(null);
    
    try {
      // If new person, create them first
      if (mode === 'new') {
        await axios.post('/api/persons', formData);
      }
      
      // Register face
      await axios.post('/api/faces/register', {
        person_id: formData.person_id,
        face_vector: faceVector,
        source_type: 'manual',
        confidence: faceConfidence,
      });
      
      setAlert({
        type: 'success',
        message: `Face registered successfully for ${formData.name}!`
      });
      
      // Refresh persons list
      fetchPersons();
      
      // Reset after short delay
      setTimeout(() => {
        resetForm();
      }, 2000);
      
    } catch (error) {
      setAlert({
        type: 'error',
        message: error.response?.data?.error || 'Error registering face'
      });
    }
    
    setIsSubmitting(false);
  };

  return (
    <div style={styles.container}>
      {/* Left: Face Capture */}
      <div style={styles.card}>
        <h2 style={styles.title}>📸 Capture Face</h2>
        <FaceCapture onFaceDetected={handleFaceDetected} mode="register" />
        
        {faceVector && (
          <div style={styles.faceData}>
            <span style={{ ...styles.badge, ...styles.badgeSuccess }}>
              ✓ Face Vector Captured
            </span>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
              128-dimensional embedding ready
            </p>
          </div>
        )}
      </div>
      
      {/* Right: Person Details Form */}
      <div style={styles.card}>
        <h2 style={styles.title}>👤 Person Details</h2>
        
        {alert && (
          <div style={{
            ...styles.alert,
            ...(alert.type === 'success' ? styles.alertSuccess : styles.alertError)
          }}>
            {alert.message}
          </div>
        )}
        
        {/* Mode Toggle */}
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button
            style={{
              ...styles.button,
              padding: '0.5rem 1rem',
              background: mode === 'new' ? 'rgba(0, 217, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
              color: mode === 'new' ? '#00d9ff' : '#fff',
            }}
            onClick={() => { setMode('new'); setSelectedPerson(null); }}
          >
            ➕ New Person
          </button>
          <button
            style={{
              ...styles.button,
              padding: '0.5rem 1rem',
              background: mode === 'existing' ? 'rgba(0, 217, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
              color: mode === 'existing' ? '#00d9ff' : '#fff',
            }}
            onClick={() => setMode('existing')}
          >
            👥 Add to Existing
          </button>
        </div>
        
        {/* Existing Persons Selection */}
        {mode === 'existing' && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={styles.label}>Select Person:</label>
            <div style={{ marginTop: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
              {existingPersons.map(person => (
                <div
                  key={person.person_id}
                  style={{
                    ...styles.personChip,
                    ...(selectedPerson?.person_id === person.person_id ? styles.personChipSelected : {})
                  }}
                  onClick={() => selectExistingPerson(person)}
                >
                  <span>{person.name}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    ({person.face_count} faces)
                  </span>
                </div>
              ))}
              {existingPersons.length === 0 && (
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                  No persons registered yet
                </p>
              )}
            </div>
          </div>
        )}
        
        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.row}>
            <div style={{ ...styles.formGroup, ...styles.halfWidth }}>
              <label style={styles.label}>Person ID *</label>
              <input
                type="text"
                name="person_id"
                value={formData.person_id}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="EMP001"
                disabled={mode === 'existing'}
                required
              />
            </div>
            <div style={{ ...styles.formGroup, ...styles.halfWidth }}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="John Doe"
                disabled={mode === 'existing'}
                required
              />
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="john@example.com"
              disabled={mode === 'existing'}
            />
          </div>
          
          <div style={styles.row}>
            <div style={{ ...styles.formGroup, ...styles.halfWidth }}>
              <label style={styles.label}>Department</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Engineering"
                disabled={mode === 'existing'}
              />
            </div>
            <div style={{ ...styles.formGroup, ...styles.halfWidth }}>
              <label style={styles.label}>Designation</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="Software Engineer"
                disabled={mode === 'existing'}
              />
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="+65 9123 4567"
              disabled={mode === 'existing'}
            />
          </div>
          
          <button
            type="submit"
            style={{
              ...styles.button,
              ...(faceVector ? styles.successButton : styles.primaryButton),
              ...(isSubmitting || !faceVector ? styles.disabledButton : {}),
              marginTop: '1rem',
            }}
            disabled={isSubmitting || !faceVector}
          >
            {isSubmitting ? '⏳ Registering...' : faceVector ? '✅ Register Face' : '📸 Capture Face First'}
          </button>
        </form>
        
        {/* Quick Stats */}
        {existingPersons.length > 0 && (
          <div style={styles.existingPersons}>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>
              📊 {existingPersons.length} persons registered • {' '}
              {existingPersons.reduce((sum, p) => sum + parseInt(p.face_count || 0), 0)} total faces
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddFacePage;
